import React, { useState, useMemo } from "react";
import { Modal, Button } from "antd";
import { svg } from "../Assets/svg";
import { decryptByText } from "../helpers/bycrypt";
import { getVatValue, VatType } from "../Admin/Pages/ClientsDistributors/clientDistributorTypes";
import { PriceTypeLabels, ServicePriceType } from "../Admin/Pages/Services/serviceTypes";
import { formatDeliveryAddress, formatGBP } from "../helpers/utils";

export interface IFieldChange {
  field: string;
  label?: string;
  previous_value: any;
  updated_value: any;
}

export interface IAffectedVariant {
  variant_name: string;
  variant_id?: string;
  reason?: string;
}

export interface ISyncIssueItem {
  id?: string;
  product_id?: string;
  name?: string;
  product_name?: string;
  category?: string;
  step_type?: "Product" | "Accessory" | "Replacement Part" | string;
  reason?: string;
  affected_variants?: IAffectedVariant[];
  changes?: IFieldChange[];
  variant_name?: string;
  [key: string]: any;
}

export interface SyncWarningItem {
  section: "products" | "storage_types" | "bespoke_items" | "services" | "delivery" | string;
  type: "deleted" | "regenerated_missing" | "delivery_reset" | string;
  severity: "WARNING" | "BLOCKING";
  title: string;
  message: string;
  items?: ISyncIssueItem[];
  sub_items?: string[]; // Fallback list
}

export interface SyncUpdateItem {
  section: "customer" | "products" | "bespoke_items" | "services" | "delivery" | string;
  type: "updated" | string;
  severity: "INFO";
  title: string;
  message: string;
  changes?: IFieldChange[];
  items?: ISyncIssueItem[];
}

export interface QuotationSyncReviewModalProps {
  open: boolean;
  actionType: "duplicate" | "revision";
  warnings?: SyncWarningItem[];
  updates?: SyncUpdateItem[];
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const safeFormatValue = (val: any, field?: string): string => {
  if (val === null || val === undefined || val === "") return "-";

  // 1. If it's an address object, format via formatDeliveryAddress
  if (typeof val === "object" && (field === "billing_address" || field === "delivery_address")) {
    return formatDeliveryAddress(val);
  }

  // 2. Decrypt string if encrypted
  let str = String(val).trim();
  if (str.includes("==") || str.startsWith("U2FsdGVk")) {
    try {
      const dec = decryptByText(str);
      if (dec && dec.trim()) str = dec.trim();
    } catch {}
  }

  // 3. Module-specific value transformations using domain helpers
  if (field === "vat_type") {
    return getVatValue(str as VatType);
  }

  if (field === "pricing_model") {
    return PriceTypeLabels[str as ServicePriceType] || str;
  }

  if (
    field === "cost" ||
    field === "unit_price" ||
    field === "selling_price" ||
    field === "service_price" ||
    field === "changeover_price" ||
    field === "delivery_price"
  ) {
    // If it's already formatted like "£...", return as is, otherwise format via formatGBP
    if (str.startsWith("£") || str.startsWith("€") || str.startsWith("$")) {
      return str;
    }
    const num = parseFloat(str);
    if (!isNaN(num)) {
      return formatGBP(num);
    }
  }

  return str;
};

interface WarningProductCardProps {
  item: ISyncIssueItem;
  itemIndex: number;
}

const WarningProductCard: React.FC<WarningProductCardProps> = ({ item, itemIndex }) => {
  const [expanded, setExpanded] = useState<boolean>(true);
  const variants = item.affected_variants || [];

  return (
    <div
      className="bg-white rounded p-3 mb-2 shadow-sm border"
      key={`warning-item-${item.product_id || item.id || itemIndex}`}
    >
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
        <div className="d-flex flex-wrap align-items-center gap-2">
          {item.category && (
            <span
              className="badge bg-light text-dark border fw-normal"
              style={{ fontSize: "11px" }}
            >
              {item.category}
            </span>
          )}
          {item.step_type && (
            <span
              className="badge bg-secondary text-uppercase fw-semibold"
              style={{ fontSize: "10px", letterSpacing: "0.5px" }}
            >
              {item.step_type}
            </span>
          )}
        </div>
        {item.reason && (
          <span
            className="badge rounded-pill bg-danger-subtle text-danger border border-danger-subtle fw-semibold px-2 py-1"
            style={{ fontSize: "11px" }}
          >
            {item.reason}
          </span>
        )}
      </div>

      <div className="fw-bold text-dark mb-2" style={{ fontSize: "13px" }}>
        {item.product_name || item.name}
      </div>

      {item.changes && item.changes.length > 0 && (
        <div className="mb-2">
          <table className="table table-sm table-bordered bg-white rounded mb-0" style={{ fontSize: "12px" }}>
            <thead>
              <tr className="table-light">
                <th style={{ width: "30%" }}>Field / Detail</th>
                <th style={{ width: "35%" }}>Previous Data</th>
                <th style={{ width: "35%" }}>Updated Data</th>
              </tr>
            </thead>
            <tbody>
              {item.changes.map((c, cIdx) => (
                <tr key={`change-${c.field || cIdx}`}>
                  <td className="text-secondary fw-semibold">{c.label || c.field}</td>
                  <td
                    className="text-danger text-decoration-line-through"
                    style={{ backgroundColor: "#fef2f2" }}
                  >
                    {safeFormatValue(c.previous_value, c.field)}
                  </td>
                  <td
                    className="fw-bold"
                    style={{ backgroundColor: "#f0fdf4", color: "#15803d" }}
                  >
                    {safeFormatValue(c.updated_value, c.field)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {variants.length > 0 && (
        <div>
          <button
            type="button"
            className="btn btn-link p-0 text-decoration-none fw-semibold d-inline-flex align-items-center gap-1"
            style={{ fontSize: "12px", color: "#475569" }}
            onClick={() => setExpanded(!expanded)}
          >
            <span>Affected Variants ({variants.length})</span>
            <span style={{ fontSize: "10px" }}>{expanded ? "▲" : "▼"}</span>
          </button>

          {expanded && (
            <div
              className="mt-2 p-2 rounded border"
              style={{ backgroundColor: "#f8fafc", fontSize: "12px" }}
            >
              <ul className="mb-0 ps-3">
                {variants.map((v, vIdx) => (
                  <li key={`variant-${v.variant_id || vIdx}`} className="py-1">
                    <span className="text-dark">{v.variant_name}</span>
                    {v.reason && (
                      <span
                        className="badge bg-white text-danger border ms-2 fw-normal"
                        style={{ fontSize: "10px" }}
                      >
                        [{v.reason}]
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const QuotationSyncReviewModal: React.FC<QuotationSyncReviewModalProps> = ({
  open,
  actionType,
  warnings = [],
  updates = [],
  loading = false,
  onConfirm,
  onClose,
}) => {
  const preparedUpdates = useMemo(() => {
    return (updates || [])
      .map((u) => {
        const filteredChanges = (u.changes || [])
          .map((c) => ({
            ...c,
            previous_formatted: safeFormatValue(c.previous_value, c.field),
            updated_formatted: safeFormatValue(c.updated_value, c.field),
          }))
          .filter((c) => c.previous_formatted !== c.updated_formatted);

        const filteredItems = (u.items || []).map((it) => {
          const itemChanges = (it.changes || [])
            .map((c) => ({
              ...c,
              previous_formatted: safeFormatValue(c.previous_value, c.field),
              updated_formatted: safeFormatValue(c.updated_value, c.field),
            }))
            .filter((c) => c.previous_formatted !== c.updated_formatted);

          return {
            ...it,
            changes: itemChanges,
          };
        });

        return {
          ...u,
          changes: filteredChanges,
          items: filteredItems,
        };
      })
      .filter((u) => (u.changes && u.changes.length > 0) || (u.items && u.items.length > 0));
  }, [updates]);

  const modalTitle =
    actionType === "duplicate"
      ? "Review Master Data Changes for Duplication"
      : "Review Master Data Changes for Revision";

  const proceedButtonText =
    actionType === "duplicate" ? "PROCEED & DUPLICATE" : "PROCEED & CREATE REVISION";

  const totalWarningsCount = warnings.length;
  const totalUpdatesCount = preparedUpdates.length;

  return (
    <Modal
      open={open}
      centered
      width="46rem"
      footer={null}
      onCancel={loading ? undefined : onClose}
      closeIcon={loading ? null : svg?.closeIcon}
      className="custom-modal"
      destroyOnClose
      title={
        <div className="text-center">
          <div
            className="fs-4 fw-bolder lh-m mb-1"
            style={{ color: "#d97706" }}
          >
            {modalTitle}
          </div>
          <p className="text-muted fw-normal mb-0" style={{ fontSize: "13px" }}>
            Some data in this quotation differs from the latest master records. Please review the
            removals and updates below before proceeding.
          </p>
        </div>
      }
    >
      <div>
        {/* Section 1: Warnings */}
        {totalWarningsCount > 0 && (
          <div className="mb-4">
            <div className="d-flex align-items-center gap-2 mb-2">
              <span
                className="badge"
                style={{
                  backgroundColor: "#f59e0b",
                  color: "#ffffff",
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                }}
              >
                WARNING
              </span>
              <span
                className="fw-bold text-uppercase"
                style={{ fontSize: "12px", color: "#78350f", letterSpacing: "0.5px" }}
              >
                ITEMS TO BE REMOVED ({totalWarningsCount})
              </span>
            </div>

            {warnings.map((w, wIdx) => (
              <div
                key={`warning-${w.section}-${wIdx}`}
                className="alert alert-warning p-3 mb-3 border-0 rounded"
                style={{
                  borderLeft: "4px solid #f59e0b",
                  backgroundColor: "rgba(245, 158, 11, 0.08)",
                }}
              >
                <div className="mb-2">
                  <div className="fw-bold text-dark" style={{ fontSize: "13px" }}>
                    {w.title}
                  </div>
                  {w.message && (
                    <div className="text-muted" style={{ fontSize: "12px" }}>
                      {w.message}
                    </div>
                  )}
                </div>

                {/* Render items if available */}
                {w.items && w.items.length > 0 ? (
                  <div>
                    {w.items.map((item, itemIdx) => {
                      const hasVariants =
                        item.affected_variants && item.affected_variants.length > 0;
                      const hasChanges = item.changes && item.changes.length > 0;

                      if (hasVariants || hasChanges || item.step_type || item.product_name) {
                        return (
                          <WarningProductCard
                            key={`warn-card-${itemIdx}`}
                            item={item}
                            itemIndex={itemIdx}
                          />
                        );
                      }

                      // Simple entity item (storage type, bespoke item, service, etc.)
                      return (
                        <div
                          key={`simple-item-${item.id || itemIdx}`}
                          className="bg-white rounded p-2 mb-2 shadow-sm border d-flex align-items-center justify-content-between"
                          style={{ fontSize: "12px" }}
                        >
                          <span className="fw-semibold text-dark">
                            • {item.name || item.product_name}
                          </span>
                          {item.reason && (
                            <span
                              className="badge bg-danger-subtle text-danger border border-danger-subtle fw-medium"
                              style={{ fontSize: "10px" }}
                            >
                              {item.reason}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : w.sub_items && w.sub_items.length > 0 ? (
                  <ul className="mb-0 ps-3 bg-white p-2 rounded border" style={{ fontSize: "12px" }}>
                    {w.sub_items.map((sub, sIdx) => (
                      <li key={`sub-${sIdx}`} className="text-dark py-1">
                        {sub}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ))}
          </div>
        )}

        {/* Section 2: Updates */}
        {totalUpdatesCount > 0 && (
          <div className="mb-3">
            <div className="d-flex align-items-center gap-2 mb-2">
              <span
                className="badge"
                style={{
                  backgroundColor: "#0284c7",
                  color: "#ffffff",
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                }}
              >
                UPDATE
              </span>
              <span
                className="fw-bold text-uppercase"
                style={{ fontSize: "12px", color: "#0369a1", letterSpacing: "0.5px" }}
              >
                SYNCHRONISED UPDATES ({totalUpdatesCount})
              </span>
            </div>

            {preparedUpdates.map((u, uIdx) => (
              <div
                key={`update-${u.section}-${uIdx}`}
                className="alert alert-info p-3 mb-3 border-0 rounded"
                style={{
                  borderLeft: "4px solid #0284c7",
                  backgroundColor: "rgba(14, 165, 233, 0.08)",
                }}
              >
                <div className="mb-2">
                  <div className="fw-bold text-dark" style={{ fontSize: "13px" }}>
                    {u.title}
                  </div>
                  {u.message && (
                    <div className="text-muted" style={{ fontSize: "12px" }}>
                      {u.message}
                    </div>
                  )}
                </div>

                {/* Top-level changes table */}
                {u.changes && u.changes.length > 0 && (
                  <div className="table-responsive mb-2">
                    <table
                      className="table table-sm table-bordered bg-white rounded mb-0"
                      style={{ fontSize: "12px" }}
                    >
                      <thead>
                        <tr className="table-light">
                          <th style={{ width: "30%" }}>Field / Detail</th>
                          <th style={{ width: "35%" }}>Previous Data</th>
                          <th style={{ width: "35%" }}>Updated Data</th>
                        </tr>
                      </thead>
                      <tbody>
                        {u.changes.map((c, cIdx) => (
                          <tr key={`u-change-${c.field || cIdx}`}>
                            <td className="text-secondary fw-semibold">
                              {c.label || c.field}
                            </td>
                            <td
                              className="text-danger text-decoration-line-through"
                              style={{ backgroundColor: "#fef2f2" }}
                            >
                              {c.previous_formatted}
                            </td>
                            <td
                              className="fw-bold"
                              style={{ backgroundColor: "#f0fdf4", color: "#15803d" }}
                            >
                              {c.updated_formatted}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Child items with changes (e.g. product variants) */}
                {u.items && u.items.length > 0 && (
                  <div>
                    {u.items.map((item, itemIdx) => (
                      <div
                        key={`u-item-${item.product_id || itemIdx}`}
                        className="bg-white rounded p-3 mb-2 shadow-sm border"
                      >
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                          {item.category && (
                            <span
                              className="badge bg-light text-dark border fw-normal"
                              style={{ fontSize: "11px" }}
                            >
                              {item.category}
                            </span>
                          )}
                          {item.step_type && (
                            <span
                              className="badge bg-secondary text-uppercase fw-semibold"
                              style={{ fontSize: "10px", letterSpacing: "0.5px" }}
                            >
                              {item.step_type}
                            </span>
                          )}
                        </div>

                        {(item.product_name || item.name) && (
                          <div className="fw-bold text-dark" style={{ fontSize: "13px" }}>
                            {item.product_name || item.name}
                          </div>
                        )}

                        {item.variant_name && (
                          <div
                            className="text-secondary mb-2 fst-italic"
                            style={{ fontSize: "12px" }}
                          >
                            Variant: {item.variant_name}
                          </div>
                        )}

                        {item.changes && item.changes.length > 0 && (
                          <div className="table-responsive mt-2">
                            <table
                              className="table table-sm table-bordered bg-white rounded mb-0"
                              style={{ fontSize: "12px" }}
                            >
                              <thead>
                                <tr className="table-light">
                                  <th style={{ width: "30%" }}>Field / Detail</th>
                                  <th style={{ width: "35%" }}>Previous Data</th>
                                  <th style={{ width: "35%" }}>Updated Data</th>
                                </tr>
                              </thead>
                              <tbody>
                                  {item.changes.map((c: any, cIdx: number) => (
                                    <tr key={`item-change-${c.field || cIdx}`}>
                                      <td className="text-secondary fw-semibold">
                                        {c.label || c.field}
                                      </td>
                                      <td
                                        className="text-danger text-decoration-line-through"
                                        style={{ backgroundColor: "#fef2f2" }}
                                      >
                                        {c.previous_formatted}
                                      </td>
                                      <td
                                        className="fw-bold"
                                        style={{ backgroundColor: "#f0fdf4", color: "#15803d" }}
                                      >
                                        {c.updated_formatted}
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action Buttons (Footer) */}
      <div className="row gx-sm-4 gx-2 justify-content-center pt-3 border-top mt-2">
        <div className="col-sm-auto col-6">
          <Button
            onClick={onClose}
            disabled={loading}
            className="btn btn-lg btn-outline-secondary w-100 h-auto px-sm-4"
          >
            CANCEL
          </Button>
        </div>
        <div className="col-sm-auto col-6">
          <Button
            onClick={onConfirm}
            loading={loading}
            className="btn btn-lg w-100 btn-primary h-auto px-sm-4 text-uppercase"
          >
            {proceedButtonText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default QuotationSyncReviewModal;

